/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useEffect,
  useRef,
  useState
} from 'react';
import {
  useLMUConfigDeviceFilePrefetch,
  UseLMUConfigDeviceFilePrefetchResult
} from '@ion/query/src/hooks/useLMUConfigDeviceFilePrefetch';

/**
 * Defines the number of prefetched device file caches for each account. There
 * will always be two caches when an account is selected, the first being for
 * the device files within the account, and the second including the device
 * files for the account and it's sub-accounts.
 */
export const PREFETCH_CACHES = 2;

/**
 * Defines the LMUConfigDeviceFilePrefetch Prop definitions, which includes
 * a reference to the accountId for which prefetching of device files is to be
 * performed, as well an includeSubAccounts prop which is used to determine if
 * the first prefetched cache is to include Device Files from the account and
 * all of it's sub accounts.
 */
export interface LMUConfigDeviceFilePrefetchProps {
  accountId: string;
  setIsGridLoaded?: Function;
  includeSubAccounts: boolean;
}

/**
 * The `LMUConfigDeviceFilePrefetch` component manages prefetching LMU Config
 * Device Files for a specified account (generally, the current Global Group),
 * as well as it's sub-accounts.
 *
 * This component is to be used for ensuring that the device files for the current
 * account and it's sub-accounts are preloaded and added to the React Query cache
 * so as to be immediately available when needed.
 *
 * The prefetching is based on two unique caches: one containing device files for
 * the account only, and another containing device files for the complete account
 * hierarchy. This ensures the immediate availability of device files when for
 * subsequent feature use for both the account and it's sub-accounts based on the
 * state of the includeSubAccounts selection.
 *
 * Usage:
 * ```typescript
 *  import LMUConfigDeviceFilePrefetch, {
 *    LMUConfigDeviceFilePrefetchProps
 *  } from '@ion/applications/src/components/jobs/LMUConfigDeviceFilePrefetch';
 *  ...
 *
 *  <LMUConfigDeviceFilePrefetch accountId={entityId(globalGroup)}
 *    includeSubAccounts={viewSubGroups} />
 * ```
 */
const LMUConfigDeviceFilePrefetch = ({
  accountId,
  setIsGridLoaded,
  includeSubAccounts
}: LMUConfigDeviceFilePrefetchProps) => {
  const prefetchCount     = useRef<number>(0);
  const previousAccountId = useRef<string>(accountId);
  const [includeHierarchy, setIncludeHierarchy] = useState<boolean>(includeSubAccounts);
  const [prefetchComplete, setPrefetchComplete] = useState<boolean>(false);

  // prefetches the current account and sub-accounts device files for pre-caching ...
  const {isLoading}: UseLMUConfigDeviceFilePrefetchResult = useLMUConfigDeviceFilePrefetch(
    accountId, includeHierarchy, (accountId !== previousAccountId.current)
  );

  // if not loading and prefetching hasn't completed, load the next cache, otherwise,
  // complete prefetching ...
  useEffect(() => {
    if (!isLoading && !prefetchComplete) {
      if (++prefetchCount.current === PREFETCH_CACHES) {
        setPrefetchComplete(true);
      } else {
        setIncludeHierarchy(!includeHierarchy);
      }
    } else {
      setIsGridLoaded?.(false);
    }
  }, [isLoading, includeHierarchy]);

  // if the current account changes, reset the state of the cache and begin prefetching
  // for the new account ...
  useEffect(() => {
    if (accountId !== previousAccountId.current) {
      previousAccountId.current = accountId;
      prefetchCount.current = 0;
      setIncludeHierarchy(includeSubAccounts);
      setPrefetchComplete(false);
    }
  }, [accountId, includeSubAccounts]);

  return undefined;
};
export default React.memo(LMUConfigDeviceFilePrefetch);
